// Eşleştirme tablosu — sayfa satırları (M16 T11; mockup 05). Kolonlar: seç · dosyadaki satır ·
// örnek değer · önerilen gösterge · güven rozeti (yüksek/orta/düşük + "önceki eşleşme" T23) ·
// kararınız (action seçici + hedef seçici). Kural 3: tablo kendi overflow-x konteynerinde.
// Kırılım (spec §7.3): "Kırılım olarak ekle" eylemi (yalnız iç sihirbaz) hedef sütununda yolu
// ("Toplam çalışan › Cinsiyete göre › Kadın (yeni)") gösterir; üç alan satırın altındaki alt
// satırda (`ImportBreakdownFields`). Kırılım grubunun toplam satırında "sistem hesaplar — kaynak
// n = alt kalemler ✓" rozeti ya da amber fark satırı (asla kırmızı).

import { Fragment } from "react";
import {
  KPI_TOTAL_ROW_NOTE,
  type KpiImportColumn,
  type KpiImportRow,
  type KpiMappingAction,
  type KpiMappingRow,
} from "@mahir/shared";
import {
  breakdownDraft,
  dimensionLabel,
  type BreakdownFieldPatch,
} from "../../../lib/kpi-import-breakdown";
import { KPI_IMPORT_COPY } from "../../../lib/kpi-import-copy";
import {
  confidenceBand,
  mappingRowStatus,
  rowSheetIndex,
  sampleValue,
  type ConfidenceBand,
} from "../../../lib/kpi-import-wizard";
import { type SearchSelectOption } from "../../common/SearchSelect";
import { inputStyle, pillStyle, tdStyle, thStyle } from "../kpi-form-styles";
import { ImportBreakdownFields } from "./ImportBreakdownFields";
import { TargetPicker, type TargetOption } from "./TargetPicker";

export interface ImportMappingTableProps {
  rows: readonly KpiMappingRow[];
  gridRows: ReadonlyMap<string, KpiImportRow>;
  columns: readonly (readonly KpiImportColumn[])[];
  selected: ReadonlySet<string>;
  onToggle: (rowId: string, checked: boolean) => void;
  onToggleAll: (checked: boolean) => void;
  onDecide: (
    rowId: string,
    patch: { action?: KpiMappingAction; targetCode?: string | null },
  ) => void;
  setOptions: readonly TargetOption[];
  catalogOptions: readonly TargetOption[];
  labelByCode: ReadonlyMap<string, string>;
  /** T19 — portalda `create` (yeni gösterge) seçeneği yok (C3) */
  allowCreate?: boolean;
  /** rowId → grid dizi indeksi (kolon modeli bu indeksle; rowId öneki gizli sayfalarda kayar). */
  sheetOfRow?: ReadonlyMap<string, number>;
  /** Kırılım (iç sihirbaz): "Kırılım olarak ekle" eylemi + alt satır alanları. Portalda verilmez. */
  breakdown?: {
    parentOptions: readonly SearchSelectOption[];
    /** Üst göstergenin bu boyutta set grubu var mı (yol ekinde "yeni" / "yeni grup"). */
    groupInSet: (parentCode: string, dimension: string) => boolean;
    /** Toplam satırı → rozet/amber satır (`totalRowLines`). */
    totalLines: ReadonlyMap<string, { matched: boolean; text: string }>;
    onPatch: (rowId: string, patch: BreakdownFieldPatch) => void;
  };
}

const ACTIONS: readonly KpiMappingAction[] = ["map", "create", "skip", "reported"];
const BREAKDOWN_ACTIONS: readonly KpiMappingAction[] = [
  "map",
  "create",
  "create_breakdown",
  "skip",
  "reported",
];

function bandStyle(band: ConfidenceBand): React.CSSProperties {
  if (band === "high") return { ...pillStyle };
  if (band === "medium")
    return {
      ...pillStyle,
      background: "var(--color-warning-bg)",
      color: "var(--color-warning-text)",
    };
  if (band === "low")
    return {
      ...pillStyle,
      background: "color-mix(in srgb, var(--color-error) 10%, white)",
      color: "var(--color-error)",
    };
  return { ...pillStyle, background: "var(--color-bg)", color: "var(--color-text-3)" };
}

function ConfidenceBadge({ row }: { row: KpiMappingRow }): JSX.Element {
  const m = KPI_IMPORT_COPY.mapping;
  const band = confidenceBand(row.confidence);
  return (
    <span className="flex flex-col gap-1 items-start">
      {row.confidence !== undefined ? (
        <span style={bandStyle(band)}>
          %{Math.round(row.confidence * 100).toString()} · {band === "none" ? "" : m.band[band]}
        </span>
      ) : row.action !== "skip" && row.targetCode ? (
        <span style={bandStyle("none")}>{m.manualNote}</span>
      ) : (
        <span style={{ color: "var(--color-text-3)" }}>—</span>
      )}
      {row.memoryHit ? <span style={bandStyle("high")}>{m.memoryHit}</span> : null}
    </span>
  );
}

export function ImportMappingTable(props: ImportMappingTableProps): JSX.Element {
  const {
    rows,
    gridRows,
    columns,
    selected,
    onToggle,
    onToggleAll,
    onDecide,
    setOptions,
    catalogOptions,
    labelByCode,
  } = props;
  const bd = props.breakdown;
  // T19 — portalda `create` yok; kırılım yalnız iç sihirbazda (C3 — müşteri yolu düşürür)
  const actions =
    props.allowCreate === false
      ? ACTIONS.filter((a) => a !== "create")
      : bd
        ? BREAKDOWN_ACTIONS
        : ACTIONS;
  const m = KPI_IMPORT_COPY.mapping;
  const allChecked = rows.length > 0 && rows.every((r) => selected.has(r.rowId));
  const colsOf = (rowId: string): readonly KpiImportColumn[] =>
    columns[props.sheetOfRow?.get(rowId) ?? rowSheetIndex(rowId)] ?? [];
  const sampleLabel = (() => {
    const first = rows[0];
    if (!first) return null;
    const gr = gridRows.get(first.rowId);
    return gr ? sampleValue(gr, colsOf(first.rowId)).label : null;
  })();

  return (
    <div style={{ overflowX: "auto", border: "1px solid var(--color-border)", borderRadius: 12 }}>
      <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 960 }}>
        <thead>
          <tr>
            <th style={{ ...thStyle, width: 36 }} scope="col">
              <input
                type="checkbox"
                aria-label={m.th.select}
                checked={allChecked}
                onChange={(e) => onToggleAll(e.target.checked)}
              />
            </th>
            <th style={{ ...thStyle, minWidth: 260 }} scope="col">
              {m.th.row}
            </th>
            <th style={thStyle} scope="col">
              {m.th.sample}
              {sampleLabel ? ` (${sampleLabel})` : ""}
            </th>
            <th style={{ ...thStyle, minWidth: 200 }} scope="col">
              {m.th.suggestion}
            </th>
            <th style={thStyle} scope="col">
              {m.th.confidence}
            </th>
            <th style={{ ...thStyle, minWidth: 360 }} scope="col">
              {m.th.decision}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const gr = gridRows.get(r.rowId);
            const sample = gr ? sampleValue(gr, colsOf(r.rowId)) : { label: null, text: "" };
            const status = mappingRowStatus(r);
            const targetLabel = r.targetCode ? labelByCode.get(r.targetCode) : undefined;
            const options = r.action === "create" ? catalogOptions : setOptions;
            const depth = gr?.depth ?? 0;
            const isBd = r.action === "create_breakdown" && bd !== undefined;
            const totalLine = bd?.totalLines.get(r.rowId);
            return (
              <Fragment key={r.rowId}>
                <tr style={status === "skipped" ? { opacity: 0.7 } : undefined}>
                  <td style={tdStyle}>
                    <input
                      type="checkbox"
                      aria-label={`${r.rowLabel} seç`}
                      checked={selected.has(r.rowId)}
                      onChange={(e) => onToggle(r.rowId, e.target.checked)}
                    />
                  </td>
                  <td style={{ ...tdStyle, paddingLeft: 12 + depth * 14 }}>
                    <div style={{ maxWidth: 320, overflowWrap: "anywhere" }} title={r.rowLabel}>
                      {r.rowLabel}
                      {r.note === KPI_TOTAL_ROW_NOTE ? (
                        <span
                          style={{ ...pillStyle, marginLeft: 6, fontSize: "0.7rem" }}
                          title={m.totalRowHelp}
                          data-testid="kpi-import-total-badge"
                        >
                          {totalLine?.matched ? totalLine.text : m.totalRowBadge}
                        </span>
                      ) : null}
                    </div>
                    {totalLine && !totalLine.matched ? (
                      <div
                        style={{ fontSize: "0.75rem", color: "var(--color-warning-text)" }}
                        data-testid="kpi-import-bd-total-line"
                      >
                        {totalLine.text}
                      </div>
                    ) : null}
                    {gr?.unitHint ? (
                      <div style={{ fontSize: "0.72rem", color: "var(--color-text-3)" }}>
                        {gr.unitHint}
                      </div>
                    ) : null}
                  </td>
                  <td
                    style={{
                      ...tdStyle,
                      color: "var(--color-text-2)",
                      whiteSpace: "nowrap",
                      maxWidth: 200,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                    title={sample.text}
                  >
                    {sample.text || "—"}
                  </td>
                  <td style={tdStyle}>
                    {isBd ? (
                      <BreakdownTarget
                        row={r}
                        groupInSet={bd.groupInSet}
                        labelByCode={labelByCode}
                      />
                    ) : r.targetCode ? (
                      <div>
                        <div>{targetLabel ?? r.targetCode}</div>
                        <div style={{ fontSize: "0.72rem", color: "var(--color-text-3)" }}>
                          <code>{r.targetCode}</code>
                          {r.note && r.note !== m.manualNote ? ` · ${r.note}` : ""}
                        </div>
                      </div>
                    ) : status === "skipped" ? (
                      <span style={{ color: "var(--color-text-3)", fontSize: "0.8rem" }}>
                        {r.note ?? "—"}
                      </span>
                    ) : (
                      <div>
                        <span style={bandStyle("low")}>{m.unmatched}</span>
                        {r.note && r.note !== m.manualNote ? (
                          <div style={{ fontSize: "0.72rem", color: "var(--color-text-3)" }}>
                            {r.note}
                          </div>
                        ) : null}
                      </div>
                    )}
                  </td>
                  <td style={tdStyle}>
                    <ConfidenceBadge row={r} />
                  </td>
                  <td style={tdStyle}>
                    <div className="flex items-start gap-2 flex-wrap">
                      <select
                        aria-label={`${r.rowLabel} kararı`}
                        style={{ ...inputStyle, minHeight: 36, padding: "6px 8px", width: 190 }}
                        value={r.action}
                        onChange={(e) =>
                          onDecide(r.rowId, { action: e.target.value as KpiMappingAction })
                        }
                      >
                        {actions.map((a) => (
                          <option key={a} value={a}>
                            {m.actions[a]}
                          </option>
                        ))}
                      </select>
                      {r.action !== "skip" && r.action !== "create_breakdown" ? (
                        <TargetPicker
                          ariaLabel={`${r.rowLabel} hedef göstergesi`}
                          value={r.targetCode}
                          options={options}
                          onChange={(code) => onDecide(r.rowId, { targetCode: code })}
                        />
                      ) : null}
                    </div>
                    <div
                      style={{ fontSize: "0.72rem", color: "var(--color-text-3)", marginTop: 2 }}
                    >
                      {m.actionHints[r.action]}
                      {r.action !== "skip" && r.action !== "create_breakdown" && !r.targetCode
                        ? ` · ${m.targetMissing}`
                        : ""}
                    </div>
                  </td>
                </tr>
                {isBd ? (
                  <tr>
                    <td style={tdStyle} />
                    <td style={{ ...tdStyle, paddingLeft: 24 }} colSpan={5}>
                      <ImportBreakdownFields
                        row={r}
                        parentOptions={bd.parentOptions}
                        onPatch={(patch) => bd.onPatch(r.rowId, patch)}
                      />
                    </td>
                  </tr>
                ) : null}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** Hedef sütunu: "Toplam çalışan › Cinsiyete göre › Kadın (yeni)" — grup da yeniyse "(yeni grup)". */
function BreakdownTarget(x: {
  row: KpiMappingRow;
  groupInSet: (parentCode: string, dimension: string) => boolean;
  labelByCode: ReadonlyMap<string, string>;
}): JSX.Element {
  const bd = breakdownDraft(x.row, null);
  if (!bd.parentCode || !bd.dimension) {
    return (
      <span style={bandStyle("low")} data-testid="kpi-import-bd-target">
        {KPI_IMPORT_COPY.mapping.unmatched}
      </span>
    );
  }
  return (
    <div data-testid="kpi-import-bd-target" style={{ overflowWrap: "anywhere" }}>
      {KPI_IMPORT_COPY.breakdown.targetPath(
        x.labelByCode.get(bd.parentCode) ?? bd.parentCode,
        dimensionLabel(bd.dimension, bd.groupLabel),
        bd.memberLabel || "—",
        !x.groupInSet(bd.parentCode, bd.dimension),
      )}
    </div>
  );
}
